import { useState, useMemo } from 'react';
import { CheckboxFilterSection, CheckboxFilterItem } from '../DynamicTable';

export interface UseTableFilteringProps {
  enableFiltering: boolean;
  checkboxFilters: CheckboxFilterSection[];
  onFiltersChange?: (filters: CheckboxFilterSection[]) => void;
  onFiltersReset?: () => void;
}

export interface UseTableFilteringReturn {
  isFilterPopupOpen: boolean;
  setIsFilterPopupOpen: (open: boolean) => void;
  activeFiltersCount: number;
  onFilterChange: (
    sectionId: CheckboxFilterSection['id'],
    item: CheckboxFilterItem,
  ) => void;
  onResetFilters: () => void;
}

export const useTableFiltering = ({
  enableFiltering,
  checkboxFilters,
  onFiltersChange,
  onFiltersReset,
}: UseTableFilteringProps): UseTableFilteringReturn => {
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);

  const activeFiltersCount = useMemo(() => {
    if (!enableFiltering || !checkboxFilters) {
      return 0;
    }

    return checkboxFilters.reduce((count, section) => {
      return count + section.items.filter((item) => item.value).length;
    }, 0);
  }, [enableFiltering, checkboxFilters]);

  const onFilterChange = (
    sectionId: CheckboxFilterSection['id'],
    item: CheckboxFilterItem,
  ) => {
    if (!onFiltersChange) {
      return;
    }

    const updatedFilters = checkboxFilters.map((section) => {
      if (section.id === sectionId) {
        const updatedItems = section.items.map((filterItem) => {
          if (filterItem.id === item.id) {
            return { ...filterItem, value: item.value };
          }

          return filterItem;
        });

        return { ...section, items: updatedItems };
      }

      return section;
    });

    onFiltersChange(updatedFilters);
  };

  const onResetFilters = () => {
    if (onFiltersReset) {
      onFiltersReset();
    }
  };

  return {
    isFilterPopupOpen,
    setIsFilterPopupOpen,
    activeFiltersCount,
    onFilterChange,
    onResetFilters,
  };
};
